import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from '../components/Header';
import Spinner from 'react-bootstrap/Spinner';
import ToastNotification from '../components/ToastNotification';
import MemberSpecific from '../components/MemberSpecific';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import '../css/Booking.scss';
import { formatKey, formatValue, ALLOWED_FIELDS } from '../components/MemberSpecific/MemberSpecificUtils';
import { getSelectedLanguage } from '../api/api';
import { languageManager } from '../utils/loadLanguage';
import { fetchStatusOptions, handleStatusChange, fetchSpecificUser } from '../utils/PackageMatchingHandlers';

const PackageMatching = () => {
  const [allPackages, setAllPackages] = useState([]);
  const [packages, setPackages] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('info');
  const [translations, setTranslations] = useState({});
  const [showSpecific, setShowSpecific] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const navigate = useNavigate();

  // Load translations
  useEffect(() => {
    async function loadLang() {
      const lang = getSelectedLanguage();
      await languageManager.loadLanguage(lang);
      setTranslations({
        ...(languageManager.translations.memberSpecificPage || {}),
        ...(languageManager.translations.specificDataKeys || {}),
        ...(languageManager.translations.common || {})
      });
    }
    loadLang();
  }, []);

  // Fetch status options for dropdown
  useEffect(() => {
    fetchStatusOptions(setStatusOptions, setIsLoadingStatus, setToastMessage, setToastVariant, setShowToast);
  }, []);

  // Fetch all packages on initial load (only once)
  useEffect(() => {
    fetchSpecificUser(setAllPackages, setPackages, setIsLoading, setToastMessage, setToastVariant, setShowToast);
  }, []);

  // Handle globalFilter changes to filter packages client-side
  useEffect(() => {
    if (globalFilter && !isNaN(globalFilter) && globalFilter.trim() !== '') {
      const filtered = allPackages.filter(pkg => pkg.user_id === parseInt(globalFilter));
      setPackages(filtered);
    } else {
      setPackages(allPackages);
    }
  }, [globalFilter, allPackages]);

  // Handle click on user_id to show MemberSpecific
  const handleViewSpecific = (userId) => {
    setSelectedUserId(userId);
    setShowSpecific(true);
  };

  // Handle click to view ReceiptPackageMatching
  const handleViewReceipt = (userId) => {
    navigate(`/receipt-package-matching/${userId}`);
  };

  // Handle cancel (back to table view)
  const handleCancelView = () => {
    setShowSpecific(false);
    setSelectedUserId(null);
  };

  // Status dropdown template for DataTable
  const statusDropdownTemplate = (rowData) => {
    const currentStatus = statusOptions.find(option => option.value === rowData.status_ID);
    return (
      <Dropdown
        value={currentStatus ? currentStatus.value : null}
        options={statusOptions}
        onChange={(e) => handleStatusChange(rowData, e.value, rowData.user_id, setToastMessage, setToastVariant, setShowToast, statusOptions)}
        placeholder={isLoadingStatus ? 'Loading...' : 'Select Status'}
        disabled={isLoadingStatus || !statusOptions.length}
        style={{ width: '150px' }}
      />
    );
  };

  // Generic column body template
  const columnBodyTemplate = (rowData, field) => {
    if (field === 'name' || field === 'sur_name') {
      console.log(`Field ${field} for user_id ${rowData.user_id}:`, rowData[field]);
    }
    return formatValue(rowData[field], field, translations, getSelectedLanguage);
  };

  // User ID column body template with clickable link
  const userIdBodyTemplate = (rowData) => {
    return (
      <span
        onClick={() => handleViewSpecific(rowData.user_id)}
        className="btn btn-secondary btn-sm"
      >
        {formatValue(rowData.user_id, 'user_id', translations, getSelectedLanguage)}
      </span>
    );
  };

  // Receipt column body template with clickable icon
  const receiptBodyTemplate = (rowData) => {
    return (
      <span
        onClick={() => handleViewReceipt(rowData.user_id)}
        className="btn btn-secondary btn-sm"
        title="View Receipt"
      >
        <FontAwesomeIcon icon={faFileInvoice} />
      </span>
    );
  };

  // Generate columns dynamically
  const columns = (() => {
    // Get user_id from packages.packages
    const userIdField = ALLOWED_FIELDS.packages.packages.find(field => field.key === 'user_id');
    // Get name and sur_name from profile.personalInfo
    const personalInfoFields = ALLOWED_FIELDS.profile.personalInfo.filter(field => ['name', 'sur_name'].includes(field.key));
    // Get remaining fields from packages.packages (excluding user_id)
    const otherPackageFields = ALLOWED_FIELDS.packages.packages.filter(field => field.key !== 'user_id');

    // คอลัมน์ Receipt
    const receiptColumn = (
      <Column
        key="receipt"
        header="Receipt"
        body={receiptBodyTemplate}
        style={{ width: '100px', textAlign: 'center' }}
      />
    );

    // Combine fields in desired order: user_id, receipt, name, sur_name, then other package fields
    const allFields = [
      userIdField,
      ...personalInfoFields,
      ...otherPackageFields,
    ].filter(Boolean);

    // สร้างคอลัมน์จาก allFields
    const dynamicColumns = [];

    // เพิ่มคอลัมน์ user_id
    if (userIdField) {
      dynamicColumns.push(
        <Column
          key={userIdField.key}
          field={userIdField.key}
          header={formatKey(userIdField.key, translations)}
          body={userIdBodyTemplate}
          sortable
          filter
          filterPlaceholder={`Search by ${formatKey(userIdField.key, translations)}`}
        />
      );
    }

    // เพิ่มคอลัมน์ Receipt ถัดจาก user_id
    dynamicColumns.push(receiptColumn);

    // เพิ่มคอลัมน์ที่เหลือ (name, sur_name, และอื่น ๆ)
    const remainingFields = allFields.filter(field => field.key !== 'user_id');
    remainingFields.forEach(field => {
      dynamicColumns.push(
        <Column
          key={field.key}
          field={field.key}
          header={formatKey(field.key, translations)}
          body={
            field.key === 'status_name' ? statusDropdownTemplate :
            (rowData) => columnBodyTemplate(rowData, field.key)
          }
          sortable
          filter
          filterPlaceholder={`Search by ${formatKey(field.key, translations)}`}
        />
      );
    });

    return dynamicColumns;
  })();

  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard-main">
          <Header
            searchQuery={globalFilter}
            setSearchQuery={setGlobalFilter}
            onBack={showSpecific ? handleCancelView : undefined}
          />
          <div className="p-datatable-details">
            {showSpecific ? (
              <MemberSpecific
                userId={selectedUserId}
                onCancel={handleCancelView}
              />
            ) : (
              <>
                {isLoading ? (
                  <div className="spinner-overlay">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">{translations.loading || "Loading..."}</span>
                    </Spinner>
                  </div>
                ) : (
                  <DataTable
                    value={packages}
                    paginator
                    rows={10}
                    globalFilter={globalFilter}
                    globalFilterFields={['user_id', 'name', 'sur_name']}
                    header={translations.packageMatching?.title || "Package Matching Data"}
                    emptyMessage={translations.noData || "No packages found"}
                    className="p-datatable-customers"
                  >
                    {columns}
                  </DataTable>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PackageMatching;